import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api.js";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { IconCheck, IconArrow } from "../components/icons.jsx";

export default function AdminTemplatesPage() {
  const { t } = useT();
  const toast = useToast();
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewTab, setPreviewTab] = useState("mail");

  // Refs to textareas for variable insertion at cursor
  const refs = {
    mail_subject: useRef(null),
    mail_html: useRef(null),
    mail_text: useRef(null),
    discord_title: useRef(null),
    discord_description: useRef(null),
  };

  async function loadList() {
    const r = await api("/admin/templates");
    setList(r.templates);
    if (!selected && r.templates.length) setSelected(r.templates[0].event_key);
  }

  async function loadOne(key) {
    const t = await api(`/admin/templates/${key}`);
    setTpl(t);
    setDirty(false);
  }

  useEffect(() => { loadList().catch((err) => toast.error(err.message)); }, []);
  useEffect(() => { if (selected) loadOne(selected).catch((err) => toast.error(err.message)); }, [selected]);

  // Live-preview using client-side substitution (instant, no API call)
  useEffect(() => {
    if (!tpl) return;
    const vars = sampleVarsFor(tpl.event_key);
    setPreview({
      mail: {
        subject: substitute(tpl.mail_subject || "", vars),
        html: substitute(tpl.mail_html || "", vars, true),
        text: substitute(tpl.mail_text || "", vars),
      },
      discord: {
        title: substitute(tpl.discord_title || "", vars),
        description: substitute(tpl.discord_description || "", vars),
        color: tpl.discord_color || "#e8243c",
      },
    });
  }, [tpl]);

  function set(field, value) {
    setTpl((t) => ({ ...t, [field]: value }));
    setDirty(true);
  }

  async function save() {
    if (!tpl) return;
    setBusy(true);
    setMessage(null);
    try {
      await api(`/admin/templates/${tpl.event_key}`, {
        method: "PUT",
        body: {
          mail_enabled: tpl.mail_enabled,
          mail_subject: tpl.mail_subject,
          mail_html: tpl.mail_html,
          mail_text: tpl.mail_text,
          discord_enabled: tpl.discord_enabled,
          discord_title: tpl.discord_title,
          discord_description: tpl.discord_description,
          discord_color: tpl.discord_color,
        },
      });
      setDirty(false);
      toast.success(t("tpl_saved"));
      // Re-fetch to get any backend-side normalizations + refresh preview
      await loadOne(tpl.event_key);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    if (!tpl) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await api(`/admin/templates/${tpl.event_key}/test-mail`, { method: "POST" });
      toast.success(t("tpl_test_sent") + " (" + r.sent_to + ")" );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  function insertVar(fieldName, varName) {
    const ref = refs[fieldName]?.current;
    if (!ref) return;
    const text = tpl[fieldName] || "";
    const start = ref.selectionStart;
    const end = ref.selectionEnd;
    const insert = `{{${varName}}}`;
    const newText = text.slice(0, start) + insert + text.slice(end);
    set(fieldName, newText);
    // Restore cursor after the inserted variable
    setTimeout(() => {
      ref.focus();
      ref.setSelectionRange(start + insert.length, start + insert.length);
    }, 0);
  }

  if (!list) return <div className="loading">{t("common_loading")}</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">{t("tpl_title")}</h1>
        </div>
        <div className="ph-meta">{t("tpl_subtitle")}</div>
      </div>

      <AnimatePresence>
        {message?.ok && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="chip ok" style={{ marginBottom: 14, padding: "10px 14px", fontSize: 12 }}>
            <IconCheck size={11} /> {message.ok}
          </motion.div>
        )}
        {message?.error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="error-line" style={{ marginBottom: 14 }}>
            {message.error}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="tpl-layout">
        {/* Left: Event selector */}
        <aside className="tpl-sidebar">
          <div className="eyebrow" style={{ padding: "0 4px 12px" }}>{t("tpl_select_event")}</div>
          {list.map((item) => (
            <button
              key={item.event_key}
              className={"tpl-event-btn " + (selected === item.event_key ? "active" : "")}
              onClick={() => {
                if (dirty && !confirm("Discard unsaved changes?")) return;
                setSelected(item.event_key);
              }}
            >
              <div className="tpl-event-name">{item.label}</div>
              <div className="tpl-event-meta">
                <span className={item.mail_enabled ? "on" : "off"}>mail {item.mail_enabled ? "✓" : "—"}</span>
                <span className={item.discord_enabled ? "on" : "off"}>discord {item.discord_enabled ? "✓" : "—"}</span>
              </div>
            </button>
          ))}
        </aside>

        {/* Middle: Editor */}
        <section className="tpl-editor">
          {tpl ? (
            <Editor tpl={tpl} t={t} set={set} refs={refs} insertVar={insertVar}
              onSave={save} onTest={sendTest} busy={busy} dirty={dirty} />
          ) : (
            <div className="loading">{t("common_loading")}</div>
          )}
        </section>

        {/* Right: Live preview */}
        <section className="tpl-preview">
          <div className="eyebrow" style={{ marginBottom: 12 }}>{t("tpl_preview")}</div>
          <div style={{ display: "flex", gap: 4, padding: 4, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: "var(--r)", marginBottom: 14 }}>
            <PreviewTab id="mail" current={previewTab} onClick={setPreviewTab}>{t("tpl_preview_mail")}</PreviewTab>
            <PreviewTab id="discord" current={previewTab} onClick={setPreviewTab}>{t("tpl_preview_discord")}</PreviewTab>
          </div>
          {preview && previewTab === "mail" && <MailPreview rendered={preview} />}
          {preview && previewTab === "discord" && <DiscordPreview rendered={preview} />}
        </section>
      </div>

      <TplStyles />
    </>
  );
}

function PreviewTab({ id, current, onClick, children }) {
  return (
    <button
      onClick={() => onClick(id)}
      style={{
        flex: 1, padding: "8px 10px",
        background: current === id ? "var(--bg-elev-2)" : "transparent",
        border: "none",
        color: current === id ? "var(--text)" : "var(--text-dim)",
        fontFamily: "var(--mono)", fontSize: 11,
        letterSpacing: "0.1em", textTransform: "uppercase",
        cursor: "pointer", borderRadius: "var(--r-sm)",
      }}
    >{children}</button>
  );
}

function Editor({ tpl, t, set, refs, insertVar, onSave, onTest, busy, dirty }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>{tpl.label}</h2>
          <p style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 4 }}>{tpl.description}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn ghost" onClick={onTest} disabled={busy}>
            {t("tpl_send_test")}
          </button>
          <button className="btn primary" onClick={onSave} disabled={busy || !dirty}>
            <IconCheck size={12} /> {busy ? "…" : t("tpl_save")}
          </button>
        </div>
      </div>

      {/* Available vars */}
      <div className="tpl-vars">
        <div className="tpl-vars-label">{t("tpl_available_vars")}</div>
        <div className="tpl-vars-list">
          {(tpl.variables || []).map((v) => (
            <code key={v} className="tpl-var-chip" title={t("tpl_vars_hint")}>{`{{${v}}}`}</code>
          ))}
        </div>
      </div>

      {/* Mail section */}
      <div className="tpl-section">
        <div className="tpl-section-head">
          <h3>📧 {t("tpl_mail")}</h3>
          <Toggle on={tpl.mail_enabled} onChange={(v) => set("mail_enabled", v)} />
        </div>

        <Field label={t("tpl_subject")}>
          <FieldInput refEl={refs.mail_subject} value={tpl.mail_subject}
            onChange={(v) => set("mail_subject", v)} vars={tpl.variables} onVar={(v) => insertVar("mail_subject", v)} />
        </Field>

        <Field label={t("tpl_html_body")} stack>
          <FieldTextarea rows={10} refEl={refs.mail_html} value={tpl.mail_html}
            onChange={(v) => set("mail_html", v)} vars={tpl.variables} onVar={(v) => insertVar("mail_html", v)} mono />
        </Field>

        <Field label={t("tpl_text_body")} stack>
          <FieldTextarea rows={5} refEl={refs.mail_text} value={tpl.mail_text}
            onChange={(v) => set("mail_text", v)} vars={tpl.variables} onVar={(v) => insertVar("mail_text", v)} mono />
        </Field>
      </div>

      {/* Discord section */}
      <div className="tpl-section">
        <div className="tpl-section-head">
          <h3>💬 {t("tpl_discord")}</h3>
          <Toggle on={tpl.discord_enabled} onChange={(v) => set("discord_enabled", v)} />
        </div>

        <Field label={t("tpl_discord_title")}>
          <FieldInput refEl={refs.discord_title} value={tpl.discord_title}
            onChange={(v) => set("discord_title", v)} vars={tpl.variables} onVar={(v) => insertVar("discord_title", v)} />
        </Field>

        <Field label={t("tpl_discord_description")} stack>
          <FieldTextarea rows={4} refEl={refs.discord_description} value={tpl.discord_description}
            onChange={(v) => set("discord_description", v)} vars={tpl.variables} onVar={(v) => insertVar("discord_description", v)} />
        </Field>

        <Field label={t("tpl_discord_color")}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="color" value={tpl.discord_color}
              onChange={(e) => set("discord_color", e.target.value)}
              style={{ width: 50, height: 36, border: "1px solid var(--line)", background: "transparent", borderRadius: "var(--r)" }} />
            <input type="text" value={tpl.discord_color}
              onChange={(e) => set("discord_color", e.target.value)} maxLength={7}
              style={{ width: 100, textTransform: "uppercase" }} className="mono" />
          </div>
        </Field>
      </div>
    </>
  );
}

function Field({ label, children, stack }) {
  return (
    <div className="field" style={stack ? {} : { marginBottom: 14 }}>
      <label style={{ display: "block", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function FieldInput({ refEl, value, onChange, vars, onVar }) {
  return (
    <div className="tpl-input-row">
      <input ref={refEl} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      <VarMenu vars={vars} onVar={onVar} />
    </div>
  );
}

function FieldTextarea({ refEl, value, onChange, rows, vars, onVar, mono }) {
  return (
    <div>
      <textarea
        ref={refEl}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={mono ? "mono" : ""}
        style={{
          width: "100%", resize: "vertical",
          fontFamily: mono ? "var(--mono)" : "inherit",
          fontSize: 12.5, lineHeight: 1.55,
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r)",
          color: "var(--text)",
          padding: "10px 12px",
        }}
      />
      <div style={{ marginTop: 6 }}>
        <VarMenu vars={vars} onVar={onVar} inline />
      </div>
    </div>
  );
}

function VarMenu({ vars, onVar, inline }) {
  if (!vars?.length) return null;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginLeft: inline ? 0 : 8 }}>
      {vars.map((v) => (
        <button key={v} type="button" onClick={() => onVar(v)} className="tpl-var-btn" title={`Insert {{${v}}}`}>
          {v}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div className={"toggle " + (on ? "on" : "")} onClick={() => onChange(!on)} role="switch" aria-checked={on}>
      <div className="knob" />
    </div>
  );
}

function MailPreview({ rendered }) {
  return (
    <div className="tpl-mail-preview">
      <div className="tpl-mail-meta">
        <span className="dim">Subject:</span> <strong>{rendered.mail.subject}</strong>
      </div>
      <div className="tpl-mail-frame">
        <div className="tpl-mail-header">
          <span className="logo">vorks<span className="dot" /><span className="con">connect</span></span>
        </div>
        <div className="tpl-mail-accent" />
        <div className="tpl-mail-body" dangerouslySetInnerHTML={{ __html: rendered.mail.html }} />
      </div>
      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: "pointer", fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-faint)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          plaintext fallback
        </summary>
        <pre style={{ marginTop: 8, padding: 10, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", fontSize: 11, color: "var(--text-mute)", whiteSpace: "pre-wrap" }}>
          {rendered.mail.text}
        </pre>
      </details>
    </div>
  );
}

function DiscordPreview({ rendered }) {
  return (
    <div className="tpl-discord-preview">
      <div className="tpl-discord-bot">
        <div className="tpl-discord-avatar">VC</div>
        <div>
          <div className="tpl-discord-name">Vorks Connect <span className="bot-tag">BOT</span></div>
          <div className="tpl-discord-time">today at {new Date().toLocaleTimeString().slice(0, 5)}</div>
        </div>
      </div>
      <div className="tpl-discord-embed" style={{ borderLeftColor: rendered.discord.color }}>
        <div className="tpl-discord-embed-title">{rendered.discord.title}</div>
        <div className="tpl-discord-embed-desc">{rendered.discord.description}</div>
      </div>
    </div>
  );
}

// ─── Client-side substitution helpers ─────────────────────────
function escHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function substitute(str, vars, htmlSafe = false) {
  if (!str) return "";
  return String(str).replace(/\{\{(\w+)\}\}/g, (_m, key) => {
    if (!(key in vars)) return `{{${key}}}`;
    return htmlSafe ? escHtml(vars[key]) : String(vars[key] == null ? "" : vars[key]);
  });
}

function sampleVarsFor(eventKey) {
  const base = {
    user_name: "Sample User",
    user_email: "user@example.com",
    base_url: window.location.origin,
  };
  switch (eventKey) {
    case "login": return { ...base, ip: "203.0.113.42", timestamp: new Date().toISOString().slice(0, 19).replace("T", " ") };
    case "new_device": return { ...base, device_name: "Office Desktop", device_os: "Windows 11", device_hostname: "DESKTOP-A1B2C3" };
    case "purchase": return { ...base, amount: "9.99 €", plan_name: "Pro", provider: "Stripe" };
    case "suspension": return { ...base, reason: "Violation of terms" };
    case "plan_change": return { ...base, plan_name: "Team" };
    default: return base;
  }
}

// Inline styles for the template editor (kept here to avoid bloating global.css)
function TplStyles() {
  return (
    <style>{`
.tpl-layout {
  display: grid;
  grid-template-columns: 240px 1fr 380px;
  gap: 16px;
  margin-top: 8px;
  align-items: start;
}
@media (max-width: 1100px) {
  .tpl-layout { grid-template-columns: 220px 1fr; }
  .tpl-preview { grid-column: 1 / -1; }
}
@media (max-width: 700px) {
  .tpl-layout { grid-template-columns: 1fr; }
  .tpl-sidebar { display: flex; flex-direction: row; overflow-x: auto; }
  .tpl-event-btn { min-width: 180px; }
}
.tpl-sidebar {
  display: flex; flex-direction: column; gap: 4px;
  position: sticky; top: 0;
  padding: 6px 0;
}
.tpl-event-btn {
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--r);
  padding: 12px 14px;
  cursor: pointer;
  transition: all 160ms;
}
.tpl-event-btn:hover { background: var(--bg-elev); border-color: var(--line); }
.tpl-event-btn.active { background: var(--bg-elev-2); border-color: var(--acc); }
.tpl-event-name {
  color: var(--text);
  font-size: 13px; font-weight: 500;
  margin-bottom: 4px;
}
.tpl-event-meta {
  display: flex; gap: 8px;
  font-family: var(--mono); font-size: 10px;
  letter-spacing: 0.05em;
}
.tpl-event-meta .on { color: var(--ok); }
.tpl-event-meta .off { color: var(--text-faint); }

.tpl-editor {
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 24px;
}
.tpl-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--line);
}
.tpl-section-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px;
}
.tpl-section-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
}

.tpl-vars {
  margin-bottom: 18px;
  padding: 12px 14px;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: var(--r);
}
.tpl-vars-label {
  font-family: var(--mono); font-size: 10px;
  color: var(--text-dim);
  letter-spacing: 0.12em; text-transform: uppercase;
  margin-bottom: 8px;
}
.tpl-vars-list {
  display: flex; gap: 6px; flex-wrap: wrap;
}
.tpl-var-chip, .tpl-var-btn {
  display: inline-block;
  padding: 4px 8px;
  background: var(--bg-elev-2);
  border: 1px solid var(--line-strong);
  border-radius: var(--r-sm);
  font-family: var(--mono);
  font-size: 11px;
  color: var(--acc);
  cursor: pointer;
}
.tpl-var-btn { color: var(--text-mute); border-style: dashed; }
.tpl-var-btn:hover { color: var(--acc); border-color: var(--acc); border-style: solid; }

.tpl-input-row {
  display: flex; gap: 8px; align-items: flex-start;
}
.tpl-input-row input { flex: 1; }

.tpl-preview {
  position: sticky; top: 12px;
}
.tpl-mail-preview, .tpl-discord-preview {
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 16px;
}
.tpl-mail-meta { font-size: 12px; margin-bottom: 10px; }
.tpl-mail-frame {
  background: #070708;
  border: 1px solid #1e1e22;
  border-radius: 8px;
  overflow: hidden;
}
.tpl-mail-header {
  background: #0e0e10; padding: 14px 18px;
  border-bottom: 1px solid #1e1e22;
}
.tpl-mail-header .logo { font-size: 14px; font-weight: 700; color: #f2f0ec; }
.tpl-mail-header .dot { display: inline-block; width: 5px; height: 5px; background: #e8243c; border-radius: 50%; margin: 0 3px 1px; }
.tpl-mail-header .con { font-family: Georgia, serif; font-style: italic; font-weight: 400; color: #e8243c; }
.tpl-mail-accent { height: 2px; background: linear-gradient(90deg, #e8243c, #c41e34); }
.tpl-mail-body {
  padding: 18px;
  color: #a09ea8; font-size: 13px; line-height: 1.55;
}
.tpl-mail-body h1 { color: #f2f0ec; font-size: 18px; margin: 0 0 12px; }
.tpl-mail-body p { margin: 0 0 12px; }
.tpl-mail-body strong { color: #f2f0ec; }

.tpl-discord-preview { background: #313338; border: none; }
.tpl-discord-bot { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 4px; }
.tpl-discord-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: linear-gradient(135deg, #e8243c, #8c1526);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 11px;
  flex-shrink: 0;
}
.tpl-discord-name { color: #f2f3f5; font-weight: 600; font-size: 14px; }
.tpl-discord-name .bot-tag {
  background: #5865f2; color: #fff;
  font-size: 9px; font-weight: 700;
  padding: 1px 4px; border-radius: 3px;
  vertical-align: middle; margin-left: 4px;
  letter-spacing: 0.04em;
}
.tpl-discord-time { color: #949ba4; font-size: 11px; margin-top: 2px; }
.tpl-discord-embed {
  margin-top: 4px;
  margin-left: 48px;
  background: #2b2d31;
  border-left: 4px solid #e8243c;
  border-radius: 4px;
  padding: 8px 12px;
  max-width: 100%;
}
.tpl-discord-embed-title {
  color: #f2f3f5; font-weight: 600; font-size: 14px; margin-bottom: 4px;
}
.tpl-discord-embed-desc {
  color: #dbdee1; font-size: 13px; line-height: 1.45;
  white-space: pre-wrap; word-break: break-word;
}
    `}</style>
  );
}
